import { resolveDocumentContentType } from '@/utils/resolveDocumentContentType';
import { toast } from '@heidi/design-system/toast';
import {
  classifyCreateEmptyDocumentFailure,
  commitCreatedDocumentToList,
  getInitialStreamingStatus,
  mapGenerationMethodToGenType,
  prefetchCreatedDocumentDetail,
  removeOptimisticDocumentDetail,
  resetSessionStreamingAfterQuotaError,
  resolveOptimisticDocumentName,
  rollbackOptimisticDocument,
  seedOptimisticDocument,
} from '@heidi/session/data';

import { shouldStartDictationAtom } from '@/atoms/shouldStartDictation';
import {
  delayUpgradeExtendAction,
  documentCreateInputBeforeDelay,
  QuotaExceedType,
  shouldShowDelayUpgradeModal,
} from '@/components/modal/delay-upgrade-modal/delayUpgradeAtoms';
import {
  IS_HEIDI_LEARNS_ENABLED,
  IS_OPTIMITCALLY_CREATE_EMPTY_DOCUMENT_ENABLED,
} from '@/features/flags';
import { openQuotaExceededUpgradeModal } from '@/features/settings/billing/utils/openQuotaExceededUpgradeModal';
import {
  type SessionWindow,
  type Tab,
  TabType,
  usePendingTabFocus,
  useSessionWindow,
  useSessionWindows,
} from '@/hooks/query/useQuerySessionWindows';
import { useIsFreeUser } from '@/hooks/useIsFreeUser';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { getDocumentById } from '@/pages/api/scribeAPI';
import { postCreateEmptyDocument } from '@/pages/api/scribeAPIV2';
import { type GetDocumentResponse } from '@/types';
import { mockEmptyDocument } from '@/types/mocks';
import { mapNewBrain } from '@/utils/brainUtils';
import { logger } from '@/utils/logger/logger';
import {
  type Brain,
  type DocumentTabType,
  GenerationMethod,
  type VoiceStyle,
} from '@heidi/schema/ml-scribe';
import { type GetDocumentsResponse } from '@heidi/session/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAtom, useSetAtom } from 'jotai';
import { useIntl } from 'react-intl';
// LOAD-BEARING side-effect import — the key this module used moved into the
// package engine at ARCFE-1004 (the ARCFE-990 rule).
import '../query/useQueryDocumentById';
import { GET_DOCUMENTS_BY_ID } from '../query/useQueryDocumentsBySessionId';
// LOAD-BEARING side-effect import — the session-row bindings this module used
// moved into the package engine at ARCFE-1004, but the legacy module must still
// be evaluated here (the ARCFE-990 rule).
import {
  documentCreatedByAICommand,
  documentCreatedByAskAI,
  documentCreatedByTemplate,
} from '@/lib/analytics/scribe/document';
import { paywallModalOpenFromQuotaExceeded } from '@/lib/analytics/scribe/payments';
import '../query/useQuerySessionById';
import { useQueryUser } from '../query/useQueryUser';

type Props = {
  templateId?: string;
  aiCommandId?: string;
  aiCommandText?: string;
  generationMethod: GenerationMethod;
  brain?: Brain;
  voiceStyle?: VoiceStyle;
  // used to optimistically update the cache
  name?: string;
  documentTabType?: DocumentTabType;
  // Optional clinical task linkage
  taskId?: string;
  /** Defaults to true. Hidden source documents should stay out of the document tab list. */
  shouldShowInDocumentTabs?: boolean;
  /**
   * Defaults to true. When false the created document's tab still appears in
   * the strip, but focus stays where it is — for background flows (queueing a
   * pinned template) that must not yank the user off their current tab.
   */
  shouldActivate?: boolean;
};

type CreateEmptyDocumentContext = {
  documents: GetDocumentsResponse | undefined;
  previousEmptyDocument: GetDocumentResponse | undefined;
  previousActiveTab: Tab | undefined;
  windowIndex: number;
};

const isSameTab = (left: Tab | undefined, right: Tab | undefined) =>
  left?.name === right?.name && left?.documentId === right?.documentId;

const commitDocumentTab = ({
  windows,
  windowIndex,
  optimisticTab,
  documentTab,
  activate,
}: {
  windows: SessionWindow[];
  windowIndex: number;
  optimisticTab: Tab;
  documentTab: Tab;
  activate: boolean;
}): SessionWindow[] =>
  windows.map((window, index) => {
    if (index !== windowIndex) {
      return window;
    }

    const optimisticTabIndex = window.tabs.findIndex((tab) =>
      isSameTab(tab, optimisticTab)
    );
    // When not activating, focus still follows the swap if the user is
    // sitting on the placeholder — its identity changes underneath them.
    const activeTab =
      activate || isSameTab(window.activeTab, optimisticTab)
        ? documentTab
        : window.activeTab;

    // No placeholder to swap: keep an already-present document tab where the
    // user has it instead of relocating it to the end of the strip.
    if (optimisticTabIndex === -1) {
      const tabs = window.tabs.some((tab) => isSameTab(tab, documentTab))
        ? window.tabs
        : [...window.tabs, documentTab];

      return {
        ...window,
        tabs,
        activeTab,
      };
    }

    const tabs = window.tabs.filter(
      (tab) => !isSameTab(tab, optimisticTab) && !isSameTab(tab, documentTab)
    );
    tabs.splice(Math.min(optimisticTabIndex, tabs.length), 0, documentTab);

    return {
      ...window,
      tabs,
      activeTab,
    };
  });

const addDocumentTabIfMissing = ({
  windows,
  windowIndex,
  documentTab,
}: {
  windows: SessionWindow[];
  windowIndex: number;
  documentTab: Tab;
}): SessionWindow[] =>
  windows.map((window, index) => {
    if (index !== windowIndex || window.tabs.some((tab) => isSameTab(tab, documentTab))) {
      return window;
    }

    return {
      ...window,
      tabs: [...window.tabs, documentTab],
    };
  });

const removeOptimisticDocumentTab = ({
  windows,
  windowIndex,
  optimisticTab,
  previousActiveTab,
}: {
  windows: SessionWindow[];
  windowIndex: number;
  optimisticTab: Tab;
  previousActiveTab: Tab | undefined;
}): SessionWindow[] =>
  windows.map((window, index) => {
    if (index !== windowIndex) {
      return window;
    }

    const tabs = window.tabs.filter((tab) => !isSameTab(tab, optimisticTab));
    if (!isSameTab(window.activeTab, optimisticTab)) {
      return {
        ...window,
        tabs,
      };
    }

    const restoredActiveTab = tabs.find((tab) => isSameTab(tab, previousActiveTab));

    return {
      ...window,
      tabs,
      activeTab:
        restoredActiveTab ?? tabs.find((tab) => tab.name === TabType.NOTE) ?? tabs.at(-1),
    };
  });

export function useMutateCreateEmptyDocument({ sessionId }: { sessionId: string }) {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const setShouldStartDictation = useSetAtom(shouldStartDictationAtom);
  const [, setDelayUpgradeModalOpen] = useAtom(shouldShowDelayUpgradeModal);
  const [, setDelayUpgradeExtendAction] = useAtom(delayUpgradeExtendAction);
  const [, setDocumentCreateInputBeforeDelay] = useAtom(documentCreateInputBeforeDelay);
  const { data: user } = useQueryUser();

  const { activeWindowIndex, setSessionWindows } = useSessionWindows({ sessionId });
  const { addTabAndSetActive, getActiveTab } = useSessionWindow({
    sessionId,
    windowIndex: activeWindowIndex,
  });
  const { requestTabFocus, requestTabFocusIfPending, clearTabFocus } = usePendingTabFocus(
    {
      sessionId,
    }
  );
  const { isFreeUser } = useIsFreeUser();
  const optimisticDocumentTab: Tab = {
    name: TabType.DOCUMENT,
    documentId: mockEmptyDocument.id,
  };

  return useMutation<
    { documentResponse: Awaited<ReturnType<typeof postCreateEmptyDocument>> },
    Error,
    Props,
    CreateEmptyDocumentContext | undefined
  >({
    mutationKey: ['createEmptyDocument', { sessionId }],
    // Serialize creates per session: concurrent mutations share the same
    // placeholder id, so interleaved commits/rollbacks corrupt each other.
    scope: { id: `createEmptyDocument:${sessionId}` },
    mutationFn: async (input: Props) => {
      const documents = queryClient.getQueryData<GetDocumentsResponse>([
        GET_DOCUMENTS_BY_ID,
        { sessionId },
      ]);
      // Create empty document
      const documentResponse = await postCreateEmptyDocument({
        contentType: resolveDocumentContentType(sessionId),
        sessionId,
        ...input,
        isPersonalizationApplied:
          (IS_HEIDI_LEARNS_ENABLED() &&
            user?.session_settings?.default_apply_personalization_insights) ??
          undefined,
        index: documents?.total,
        taskId: input.taskId,
      });
      documentResponse.data.brain = mapNewBrain(documentResponse.data.brain, isFreeUser);

      return { documentResponse };
    },
    onSuccess: async ({ documentResponse }, input, context) => {
      if (input.shouldShowInDocumentTabs !== false) {
        commitCreatedDocumentToList(queryClient, {
          sessionId,
          placeholderId: mockEmptyDocument.id,
          document: documentResponse.data,
        });

        removeOptimisticDocumentDetail(queryClient, {
          sessionId,
          placeholderId: mockEmptyDocument.id,
        });

        const documentTab: Tab = {
          name: TabType.DOCUMENT,
          documentId: documentResponse.data.id,
        };
        setSessionWindows((windows) =>
          commitDocumentTab({
            windows,
            windowIndex: context?.windowIndex ?? activeWindowIndex,
            optimisticTab: optimisticDocumentTab,
            documentTab,
            // Optimistic path already activated the placeholder in onMutate, so
            // here we only swap the id and let focus follow if the user is still
            // sitting on it (commitDocumentTab's `activeTab === optimistic` check).
            // Force-activating would override a manual switch made mid-create.
            // The non-optimistic path never activated, so this completion is the
            // legitimate "take me to it" claim.
            activate: context ? false : input.shouldActivate !== false,
          })
        );
        // "Create a tab, take me to it" — mark the real tab as the focus intent
        // so reconciliation keeps it active through the documents-query lag
        // instead of bouncing back to the default tab.
        if (input.shouldActivate !== false) {
          if (context) {
            // Optimistic path: onMutate already claimed focus on the placeholder.
            // Only swap the intent to the real id if it is still ours — a manual
            // tab switch during creation clears it, and we must not fight that.
            requestTabFocusIfPending(optimisticDocumentTab, documentTab);
          } else {
            // Non-optimistic path set no prior intent; this completion is the
            // "take me to it" claim.
            requestTabFocus(documentTab);
          }
        }
      }

      await prefetchCreatedDocumentDetail(queryClient, {
        sessionId,
        documentId: documentResponse.data.id,
        fetchDocument: getDocumentById,
        streamingStatus: getInitialStreamingStatus(
          input.documentTabType,
          input.generationMethod
        ),
        onPrefetchFailed: (detailError) => {
          // The tab's own document query retries the detail fetch on mount.
          logger.error('Failed to prefetch created document detail:', detailError);
        },
      });

      if (input.generationMethod === GenerationMethod.LIVE_DICTATION) {
        setShouldStartDictation(documentResponse.data.id);
      }

      if (input.shouldShowInDocumentTabs !== false) {
        // tracking (old system - kept for backward compatibility)
        switch (input.generationMethod) {
          case GenerationMethod.AI_COMMAND_TEXT:
            documentCreatedByAskAI(input.aiCommandText);
            break;
          case GenerationMethod.PRESET_AI_COMMAND:
            documentCreatedByAICommand(input.aiCommandId);
            break;
          case GenerationMethod.TEMPLATE:
            documentCreatedByTemplate(input.templateId);
            break;
          default:
            break;
        }

        // New unified tracking system
        const generationMethodMap = {
          [GenerationMethod.GPCCMP]: 'gpccmp' as const,
          [GenerationMethod.TEMPLATE]: 'template' as const,
          [GenerationMethod.INTERNAL_TEMPLATE]: 'internal_template' as const,
          [GenerationMethod.THIRD_PARTY_TEMPLATE]: 'third_party_template' as const,
          [GenerationMethod.AI_COMMAND_TEXT]: 'ask_ai' as const,
          [GenerationMethod.PRESET_AI_COMMAND]: 'ai_command' as const,
          [GenerationMethod.LIVE_DICTATION]: 'live_dictation' as const,
          [GenerationMethod.DICTATION]: 'dictation' as const,
          [GenerationMethod.PDF]: 'pdf' as const,
        };

        const methodKey =
          generationMethodMap[input.generationMethod as keyof typeof generationMethodMap];
        if (methodKey) {
          track({
            options: { amplitude: false },
            event_name: TrackingEventName.DocumentCreated,
            props: {
              session_id: sessionId,
              document_id: documentResponse.data.id,
              generation_method: methodKey,
            },
          });
        }
      }
    },
    onMutate: async (input): Promise<CreateEmptyDocumentContext | undefined> => {
      // optimistically update the cache
      if (
        !IS_OPTIMITCALLY_CREATE_EMPTY_DOCUMENT_ENABLED() ||
        input.shouldShowInDocumentTabs === false
      ) {
        return;
      }

      const previousActiveTab = getActiveTab();
      const optimisticName = resolveOptimisticDocumentName(input);

      const { documents, previousEmptyDocument } = await seedOptimisticDocument(
        queryClient,
        {
          sessionId,
          placeholderId: mockEmptyDocument.id,
          seed: {
            ...mockEmptyDocument,
            name: optimisticName,
            streamingStatus: getInitialStreamingStatus(
              input.documentTabType,
              input.generationMethod
            ),
          },
          optimisticRow: {
            ...mockEmptyDocument,
            session_id: sessionId,
            name: optimisticName,
            generation_type: mapGenerationMethodToGenType(input.generationMethod),
          },
        }
      );

      if (input.shouldActivate === false) {
        setSessionWindows((windows) =>
          addDocumentTabIfMissing({
            windows,
            windowIndex: activeWindowIndex,
            documentTab: optimisticDocumentTab,
          })
        );
      } else {
        addTabAndSetActive(optimisticDocumentTab);
        // Hold focus on the optimistic tab too, so an in-flight reconcile can't
        // bounce off it before onSuccess swaps in the real id.
        requestTabFocus(optimisticDocumentTab);
      }

      return {
        documents,
        previousEmptyDocument,
        previousActiveTab,
        windowIndex: activeWindowIndex,
      };
    },
    onError: (error, input, context) => {
      if (context) {
        rollbackOptimisticDocument(queryClient, {
          sessionId,
          placeholderId: mockEmptyDocument.id,
          snapshot: context.documents,
          previousEmptyDocument: context.previousEmptyDocument,
        });

        setSessionWindows((windows) =>
          removeOptimisticDocumentTab({
            windows,
            windowIndex: context.windowIndex,
            optimisticTab: optimisticDocumentTab,
            previousActiveTab: context.previousActiveTab,
          })
        );
        // The tab we asked to focus no longer exists — drop the intent so it
        // doesn't linger against the restored active tab. Identity-guarded so we
        // only clear our own optimistic intent, never one a concurrent flow set.
        clearTabFocus(optimisticDocumentTab);
      }

      const failure = classifyCreateEmptyDocumentFailure(
        error,
        (e): e is AxiosError => e instanceof AxiosError
      );

      if (failure.kind === 'unlimited-quota-until-unset') {
        resetSessionStreamingAfterQuotaError(queryClient, sessionId);
        setDelayUpgradeModalOpen(true);
        setDelayUpgradeExtendAction(QuotaExceedType.CREATE_DOCUMENT);
        // Stash the input so the modal's "extend" path can replay the create.
        if (input.aiCommandId || input.templateId) {
          setDocumentCreateInputBeforeDelay(input);
        }
        return;
      }

      if (failure.kind === 'quota-exceeded') {
        resetSessionStreamingAfterQuotaError(queryClient, sessionId);
        openQuotaExceededUpgradeModal();
        paywallModalOpenFromQuotaExceeded();
        return;
      }

      if (failure.kind === 'request-failed') {
        toast({
          tone: 'destructive',
          title: intl.formatMessage({
            defaultMessage: 'Failed to create empty document',
            id: 'IE/T+Qjuzl',
          }),
          description: failure.message,
        });
      } else {
        // Non-Axios failures (e.g. a client-side throw after the POST) must
        // not stay silent — the rollback already made the tab vanish.
        logger.error('Failed to create empty document:', error);
        toast({
          tone: 'destructive',
          title: intl.formatMessage({
            defaultMessage: 'Failed to create empty document',
            id: 'IE/T+Qjuzl',
          }),
          description: intl.formatMessage({
            defaultMessage: 'Please try again',
            id: 'fHqssj/1KO',
          }),
        });
      }
    },
  });
}
